"use client";

import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DataTableToolbar } from "./data-table-toolbar";
import { PanelTopClose, PanelTopOpen } from "lucide-react";
import Papa from "papaparse";
import { CraftOpportunity } from "./craft-columns";

interface CraftDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function CraftDataTable<TData extends CraftOpportunity, TValue>({
  columns,
  data,
}: CraftDataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [search, setSearch] = React.useState("");
  const [hide, setHide] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState<string>("all");

  // Build year options dynamically from loaded opportunities
  const availableYears = React.useMemo(() => {
    const years = new Set<string>();

    data.forEach((opportunity) => {
      const dateField = opportunity.created_on || opportunity.createdAt;
      if (!dateField) return;

      const date = new Date(dateField);
      if (!Number.isNaN(date.getTime())) {
        years.add(date.getFullYear().toString());
      }
    });

    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [data]);

  // Filter data based on selectedYear
  const filteredDataByYear = React.useMemo(() => {
    if (selectedYear === "all") {
      return data;
    }

    return data.filter((opportunity) => {
      const dateField = opportunity.created_on || opportunity.createdAt;
      if (!dateField) {
        return true;
      }
      try {
        const createdYear = new Date(dateField).getFullYear().toString();
        return createdYear === selectedYear;
      } catch (e) {
        console.error("Error parsing date for opportunity:", opportunity.id, dateField, e);
        return false;
      }
    });
  }, [data, selectedYear]);

  const table = useReactTable({
    data: filteredDataByYear,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Always show all rows by setting the page size to the total filtered rows
  React.useEffect(() => {
    const totalRows = table.getFilteredRowModel().rows.length;
    table.setPageSize(totalRows > 0 ? totalRows : 1);
    table.setPageIndex(0);
  }, [table, filteredDataByYear, selectedYear, search, columnFilters, sorting]);

  // Search handler
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    table.getColumn("name")?.setFilterValue(value);
  };

  // Utility functions for formatting
  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return "N/A";
    try {
      const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount: number | string | null | undefined): string => {
    if (amount === null || amount === undefined) return "N/A";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return "Invalid Amount";
    return new Intl.NumberFormat('en-US', { style: "currency", currency: "USD" }).format(numAmount);
  };

  // Mapping function for export
  const mapDataForExport = (exportTableData: TData[]) => {
    return exportTableData.map((row) => ({
      Created: formatDate(row.created_on || row.createdAt),
      "Deal Name": row.name || "N/A",
      Account: "Craft Capital",
      Amount: formatCurrency(row.budget),
      City: row.assigned_leads?.city || "N/A",
      State: row.assigned_leads?.state || "N/A",
      Advisor: row.assigned_leads?.advisorName || "N/A",
      "Investor Name": row.assigned_leads 
        ? `${row.assigned_leads.firstName || ""} ${row.assigned_leads.lastName || ""}`.trim() || "N/A"
        : "N/A",
    }));
  };

  // CSV Export Function
  const handleExport = () => {
    const currentTableData = table.getFilteredRowModel().rows.map(row => row.original);
    const exportData = mapDataForExport(currentTableData);

    const csvHeaders = [
      "Created", "Deal Name", "Account", "Amount",
      "City", "State", "Advisor", "Investor Name",
    ];

    const csv = Papa.unparse({
      fields: csvHeaders,
      data: exportData.map((row) => [
        row.Created, row["Deal Name"], row.Account, row.Amount,
        row.City, row.State, row.Advisor, row["Investor Name"],
      ]),
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `craft_capital_deals_${selectedYear === "all" ? "all_years" : selectedYear}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by deal name..."
            className="max-w-sm h-9"
          />
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[120px] h-9">
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {availableYears.map((year) => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          <Button onClick={handleExport} variant="secondary" size="sm">
            Export CSV
          </Button>

          {hide ? (
            <Button variant="ghost" size="icon" onClick={() => setHide(false)} title="Show Table">
              <PanelTopOpen className="w-5 h-5 text-muted-foreground" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" onClick={() => setHide(true)} title="Hide Table">
              <PanelTopClose className="w-5 h-5 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>

      {hide ? (
        <div className="flex items-center gap-2 p-4 border rounded-md text-muted-foreground">
          Table content is hidden. Click the <PanelTopOpen className="inline w-5 h-5 mx-1" /> icon to show.
        </div>
      ) : (
        <>
          <DataTableToolbar table={table} />
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results for {selectedYear}.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
} 